import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthMeResponse, AuthResponse } from '../modules/auth/interfaces/auth.interfaces';
import { CoreService } from './core.service';
import { LocalStorageService } from './local-storage.service';

export type PermissionsUser = {
	[module_name: string]: {
		access: boolean;
		submodules: {
			name: string;
			access: boolean;
		}[];
	};
};

export type SubRole = {
	action: string;
	id: string;
	module: string;
	submodule: string;
};

export type LoginResponse = {
	accessToken: string;
	refreshToken: string;
	role: string;
	name: string;
	userId: string;
	institutionId: string;
	identifier: string;
	permissions: PermissionsUser;
	subroles: SubRole[];
};
export interface LoginValleLabResponse {
	permissions: Permissions;
	refreshToken: string;
	subRole: string;
	subroles: {
		action: string;
		id: string;
		module: string;
		submodule: string;
	}[];
	token: string;
	userId: string;
}

export interface Permissions {
	hvi: Hvi;
}

export interface Hvi {
	access: boolean;
	submodules: SubModule[];
}
export interface SubModule {
	access: boolean;
	name: string;
}

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private gatewayURL: string = environment.gatewayURL;
	private authStatusSubject = new BehaviorSubject<boolean>(false);
	public authStatus$ = this.authStatusSubject.asObservable();

	constructor(private ls: LocalStorageService, private http: HttpClient, private coreService: CoreService) { }

	checkToken(): Observable<boolean> {
		const token = this.ls.getItem('bearer');
		return this.http.get<{ data: AuthMeResponse }>(`${this.gatewayURL}/v1/auth/me`, {
			headers: {
				Authorization: `Bearer ${token}`,
			}
		}).pipe(
			switchMap(({ data }: { data: AuthMeResponse }) => {
				const authenticated = !!data.id;
				if (authenticated) {
					this.handleResponse(data);
					this.authStatusSubject.next(true);
				}
				return of(authenticated);
			}),

			catchError((_) => {
				this.authStatusSubject.next(false);
				return of(false);
			}),
		);
	}

	login(data: {
		email: string;
		password: string;
	}) {
		return this.http
			.post<{ data: AuthResponse }>(`${this.gatewayURL}/v1/auth/login`, data)
			.pipe(
				tap(({ data }: { data: AuthResponse }) => {
					this.ls.saveItem('bearer', data.accessToken);
					this.ls.saveItem('refresh', data.refreshToken);

					const expiresInDate = new Date();
					expiresInDate.setSeconds(expiresInDate.getSeconds() + data.expiresIn);
					this.ls.saveItem('expiresInDate', expiresInDate.toISOString());
					this.saveLocalLists();
				}),
				switchMap(() => this.checkToken()),
			);
	}

	refreshToken() {
		return this.http
			.post<{ data: AuthResponse }>(`${this.gatewayURL}/v1/auth/refresh`, {
			refreshToken: this.ls.getItem('refresh'),
		}).pipe(
			tap(({data}: { data: AuthResponse}) => {
				this.ls.saveItem('bearer', data.accessToken);
				this.ls.saveItem('refresh', data.refreshToken);

				const expiresInDate = new Date();
				expiresInDate.setSeconds(expiresInDate.getSeconds() + data.expiresIn);
				this.ls.saveItem('expiresInDate', expiresInDate.toISOString());
			}),
		);
	}

	valleLabRefreshToken() {
		/*
		return this.http
			.post<LoginValleLabResponse>(`@valleLabApi/auth/refresh`, {
			refreshToken: this.ls.getItem('refreshTokenValleLab'),
		})
			.pipe(
				tap((response) => {
					this.valleLabApiResponse(response, this.ls);
				}),
			);
			*/
		return this.refreshToken();
	}

	logout() {
		return this.http.post(`${this.gatewayURL}/v1/auth/logout`, {
			refreshToken: this.ls.getItem('refresh'),
		}).pipe(
			tap(() => {
				this.clearSession();
				this.authStatusSubject.next(false);
			}),
		);
	}

	resetPassword(email: string) {
		return this.http.post<{ success: boolean; message: string }>(`${this.gatewayURL}/v1/auth/forgot-password`, {
			email,
		});
	}

	checkPasswordResetToken(token: string) {
		return this.http.post<{ success: boolean; message: string; data: { valid: boolean, used: boolean } }>(`${this.gatewayURL}/v1/auth/check-password-reset-token`, {
			token,
		}).pipe(
			map((response) => response.data)
		);
	}

	changePassword(token: string, password: string) {
		return this.http.post<{ success: boolean; message: string }>(`${this.gatewayURL}/v1/auth/set-password`, {
			token,
			password,
			documentVersion: '1'
		});
	}

	clearSession() {
		this.ls.clear();
	}

	private handleResponse(
		response: AuthMeResponse,
	) {
		this.ls.saveItem('name', response.name);
		this.ls.saveItem('userId', response.id);
		this.ls.saveItem('identifier', response.identifier);

		this.ls.saveItem('institutions', JSON.stringify(response.institutionIds));

		// For legacy support, we save the first institutionId in a separate key
		this.ls.saveItem('institutionId', response.institutionIds[0] || '');
		this.ls.saveItem('role', response.type);
		if(response.subType){
			this.ls.saveItem('subType', response.subType);
		}

		const mappedPermissions = response.roles.map((role) => {
			return role.permissions.map((permission) => {
				const permissions = permission.permission.name.split('.');
				const moduleName = permissions[0];
				let subModuleName = '';
				let action = '';
				if(permissions.length === 3){
					subModuleName = permissions[1];
					action = permissions[2];
				}else{
					subModuleName = '';
					action = permissions[1];
				}
				return {
					name: permission.permission.name,
					type: permission.permission.type,
					moduleName,
					subModuleName,
					action,
				};
			});
		}).flat();
		this.ls.saveItem('permissions', JSON.stringify(mappedPermissions));
		const isInstitutionalResumePermissions = this.getInstitutionalResumePermissions(response);
		this.ls.saveItem(
			'isHviPermissions',
			isInstitutionalResumePermissions ? 'true' : 'false',
		);
	}

	/*
	private getInstitutionalResumePermissions(token: string): boolean {
		if (!token) return false;

		let decode;
		try {
			const payload = token.split('.')[1];
			if (!payload) return false; // Verificar si el payload existe
			const decodedPayload = window.atob(payload);
			decode = JSON.parse(decodedPayload);
		} catch (e) {
			return false;
		}
		return this.hasAdminRole(decode) || this.hasInstitutionalRoles(decode);
	}*/

	/*
	private hasAdminRole(decode: any): boolean {
		if (decode.hasOwnProperty('roles')) {
			return decode.roles.hasOwnProperty('admin');
		}
		if (decode.hasOwnProperty('role')) {
			const roles = decode.role;
			return roles === 'admin';
		}
		return false;
	}*/

	/*
	private hasInstitutionalRoles(decode: any): boolean {
		return (
			decode.hasOwnProperty('roles') &&
			(decode.roles.hasOwnProperty('secretary') ||
				decode.roles.hasOwnProperty('institution'))
		);
	}*/

	private getInstitutionalResumePermissions(data: AuthMeResponse): boolean {
		return this.hasAdminRole(data) || this.hasInstitutionalRoles(data);
	}

	private hasAdminRole(data: AuthMeResponse): boolean {
		return data.type === 'admin';
	}

	private hasInstitutionalRoles(data: AuthMeResponse): boolean {
		return data.type === 'secretary' || data.type === 'institution';
	}

	/*
	private valleLabApiResponse(
		loginValleLab: LoginValleLabResponse,
		localStorage: LocalStorageService,
	) {
		const subroles: any[] = JSON.parse(this.ls.getItem('subroles', '[]')) || [];
		let permissions = JSON.parse(this.ls.getItem('permissions', '{}'));
		const nonHviSubRoles = subroles.filter(
			(subRole) => subRole.module !== 'hvi',
		);

		const hviSubRoles = loginValleLab.subroles;

		this.ls.saveItem('hviRole', loginValleLab.subRole);
		const dashboardHviSubrule: {
			action: string;
			id: string;
			module: string;
			submodule: string;
		} = {
			action: 'view_dashboard',
			id: '4c2d8f9a-5171-4fa1-9ef9-5ab9c728db9f',
			module: 'hvi',
			submodule: 'dashboard',
		};
		hviSubRoles.push(dashboardHviSubrule);
		const hviPermissions: Hvi = {
			access: loginValleLab.permissions.hvi.access,
			submodules: [
				...loginValleLab.permissions.hvi.submodules,
				{
					access: loginValleLab.permissions.hvi.access,
					name: 'dashboard',
				},
				{
					name: 'pei',
					access: true,
				},
			],
		};

		permissions['institutional-resume'] = hviPermissions;
		const subRoles = [...nonHviSubRoles, ...hviSubRoles];

		localStorage.saveItem('tokenValleLab', loginValleLab.token);
		localStorage.saveItem('refreshTokenValleLab', loginValleLab.refreshToken);

		localStorage.saveItem('subroles', JSON.stringify(subRoles));
		localStorage.saveItem('permissions', JSON.stringify(permissions));
	}*/

	private saveLocalLists(){
		forkJoin([
			this.coreService.getUserTypes(),
			this.coreService.getUserSubTypes(),
		]).subscribe(([userTypes, userSubTypes]) => {
			this.ls.saveItem('userTypes', JSON.stringify(userTypes));
			this.ls.saveItem('userSubTypes', JSON.stringify(userSubTypes));
		});
	}
}
